import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useLogin } from "../../authConfig";
import { useClinicalFilters } from "../../clinicalContext";

import { LoginButton } from "../../components/LoginButton";
import { SHOW_AUDIT, SHOW_BRIEF, SHOW_SUMMARIES } from "../../featureFlags";
import { IconButton } from "@fluentui/react";

function displayClinicalLabel(value: string | null | undefined): string {
    if (!value) return "";
    return value
        .replace(/\bSYN-(?:PAT|NOTE|BRIEF)-[A-Za-z0-9_.:-]+/gi, "")
        .replace(/\bsynthetic\b/gi, "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

const Layout = () => {
    const { t } = useTranslation();
    const { filters, caseloadSelection } = useClinicalFilters();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef: RefObject<HTMLDivElement> = useRef(null);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    <Link to="/" className={styles.headerTitleContainer}>
                        <h3 className={styles.headerTitle}>
                            AI Search
                            {SHOW_SUMMARIES
                                ? (displayClinicalLabel(filters.patientName) ? <span className={styles.patientName}> &bull; {displayClinicalLabel(filters.patientName)}</span> : null)
                                : (displayClinicalLabel(caseloadSelection.team) ? <span className={styles.patientName}> &bull; {displayClinicalLabel(caseloadSelection.team)}</span> : null)
                            }
                        </h3>
                    </Link>
                    <nav>
                        <ul className={`${styles.headerNavList} ${menuOpen ? styles.show : ""}`}>
                            <li>
                                <NavLink
                                    to="/caseload"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Caseload
                                </NavLink>
                            </li>
                            {SHOW_BRIEF && (
                                <li>
                                    <NavLink
                                        to="/brief"
                                        end
                                        className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {t("brief")}
                                    </NavLink>
                                </li>
                            )}
                            <li>
                                <NavLink
                                    to="/browse"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {t("browse")}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/trajectory"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Trajectory
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/chat"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {t("chat")}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/qa"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {t("qa")}
                                </NavLink>
                            </li>
                            {SHOW_AUDIT && (
                                <li>
                                    <NavLink
                                        to="/audit"
                                        className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        Audit
                                    </NavLink>
                                </li>
                            )}
                        </ul>
                    </nav>
                    <div className={styles.loginMenuContainer}>
                        <NavLink
                            to="/help"
                            className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                            style={{ marginRight: "16px" }}
                        >
                            {t("helpPage.nav")}
                        </NavLink>

                        {useLogin && <LoginButton compact />}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>
                </div>
            </header>

            <main className={styles.main} id="main-content">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
